import React, { useContext } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import Group from 'components/utils/Group'
import Pill from 'components/Luxkit/Pill'
import config from 'constants/config'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import FormatCurrency from 'components/Common/FormatCurrency'
import TaxesAndFees from 'components/OfferPage/PackageOption/Pricing/TaxesAndFees'
import { connect } from 'react-redux'
import useHotelPriceColour from 'hooks/useHotelPriceColour'
import GeoContext from 'contexts/geoContext'
import TextButton from 'components/Luxkit/Button/TextButton'
import PriceDetailsModal from 'components/Common/PriceDetails/PriceDetailsModal'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import Caption from 'components/Luxkit/Typography/Caption'
import { buildSuggestedDatesParamsKey, buildSuggestedDatesString } from 'lib/search/searchUtils'
import { getSuggestedDates } from 'selectors/offerSelectors'
import BusinessTravellerOfferCreditsTextLink from 'businessTraveller/components/offer-credits/BusinessTravellerOfferCreditsTextLink'
import useShowNewHotelSearch from 'components/SearchV2/Hooks/useShowNewHotelSearch'
import cn from 'clsx'
import PaletteProvider from 'contexts/PaletteProvider'
import { pluralizeToString } from 'lib/string/pluralize'
import useBedbankFlightPrice from 'hooks/OfferPage/useBedbankFlightPrice'
import { getDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import useBedbankOfferSell from 'hooks/useBedbankOfferSell'
import BedbankTwinShare from 'components/OfferList/OfferListTiles/Support/BedbankTwinShare'

const Root = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  align-items: flex-start;

  &.upsell {
    align-items: flex-start;
  }
  &.newTile {
    align-items: flex-start;
    align-self: flex-start;
  }
`

const StyledPill = styled(Pill)`
  margin: ${rem(4)} 0 0 ${rem(4)};
`

const UpsellCTAButton = styled(TextButton)`
  align-self: flex-end;
`

interface MappedStateProps {
  suggestedDates: App.OfferSuggestedDates,
}

interface Props {
  rate: App.BedbankRate;
  rooms: Array<App.Occupants>;
  offer: App.BedbankOffer | App.BedbankOfferSummary;
  filters?: App.OfferListFilters;
  offerUrl: string;
  promotions: App.BedbankPromotion[];
  isUpsell?: boolean;
  flightsEnabled: boolean;
}

function BedbankOfferTilePriceDetails(props: Props & MappedStateProps) {
  const {
    rate,
    rooms,
    offer,
    filters,
    offerUrl,
    suggestedDates,
    isUpsell,
    flightsEnabled,
    promotions,
  } = props

  const showNewTileDesign = useShowNewHotelSearch()

  const { showTaxExclusivePrices } = useContext(GeoContext)
  const isFlexibleSearch = filters?.flexibleNights && suggestedDates?.checkIn && suggestedDates?.checkOut

  let duration = rate?.nights
  if (promotions.length && !filters?.checkIn && !filters?.checkOut) {
    duration = Math.min(...promotions.map(x => x.los))
  }
  const sell = useBedbankOfferSell(offer, duration, rooms)

  const taxesAndFees = rate?.totals?.taxesAndFees ?? 0
  const propertyFees = rate?.totals?.propertyFees ?? 0
  const hotelPrice = suggestedDates?.price ?? rate ? showTaxExclusivePrices ? rate.totals.exclusive : rate.totals.inclusive : sell?.price
  const hotelValue = suggestedDates?.value ?? rate?.value ?? sell?.value
  const hotelDiscount = rate?.discount ?? sell?.discount

  const hotelNights = rate?.nights ?? sell?.los

  const flightPrice = useBedbankFlightPrice(offer, rooms, flightsEnabled, filters?.checkIn, filters?.checkOut)

  const exclusivePriceColor = useHotelPriceColour(taxesAndFees)

  const saleUnit = offer.displayPricingAsPerPerson ? 'person' : 'room'

  const totalPrices = getDisplayTotalPrice({
    hotelPrice: showTaxExclusivePrices ? hotelPrice : hotelPrice + propertyFees,
    hotelValue,
    checkIn: filters?.checkIn,
    flightPrice,
    rooms,
    displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
  })

  const promoteAsBundle = flightsEnabled && !!flightPrice && offer.promoteAsBundle

  return <Root className={cn({ upsell: isUpsell, newTile: showNewTileDesign })}>
    <Caption variant={!isUpsell ? 'medium' : 'large'} colour="neutral-two">
      {isFlexibleSearch && buildSuggestedDatesString(suggestedDates.checkIn, suggestedDates.checkOut)}
      {!isFlexibleSearch && `Total for ${pluralizeToString('night', hotelNights)}`}{promoteAsBundle && ' + flights'}
      {(rooms?.length ?? 0) > 0 && !offer.displayPricingAsPerPerson && `, ${formatOccupantsShort(rooms)} from`}
    </Caption>
    <Group verticalAlign="center" direction="vertical">
      <Heading variant="heading3" colour={exclusivePriceColor}>
        <FormatCurrency value={totalPrices.totals.price + (promoteAsBundle ? totalPrices.totals.flight : 0)} format="roundedDollar" />
        {offer.displayPricingAsPerPerson && <Caption variant="medium" as="span">/{saleUnit}</Caption>}
        {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
      </Heading>
    </Group>
    {taxesAndFees > 0 && (<TaxesAndFees taxAmount={taxesAndFees} />)}
    {hotelDiscount > 0 &&
      <BodyText variant="small" weight="semi-bold" colour="neutral-one">
        Valued up to <FormatCurrency value={totalPrices.totals.value + (promoteAsBundle ? totalPrices.totals.flight : 0)} format="roundedDollar" />
        {hotelDiscount > config.displayDiscountThreshold.bedbank && !isUpsell &&
          <PaletteProvider type="default">
            <StyledPill kind="primary" variant="success">
              {Math.round(hotelDiscount * 100)}% off
            </StyledPill>
          </PaletteProvider>
        }
      </BodyText>
    }

    {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
      type="estimate"
      offer={offer}
      totalCost={totalPrices.original.price + (promoteAsBundle ? totalPrices.original.flight : 0)}
      numberOfNights={hotelNights}
    />}

    {isUpsell && <UpsellCTAButton kind="primary" fit={showNewTileDesign ? 'full-width' : 'auto'} to={offerUrl}>
      View Offer
    </UpsellCTAButton>}

    {rate && !isUpsell && (<PriceDetailsModal
      offer={offer}
      rooms={rooms}
      propertyFees={propertyFees}
      hotelPrice={hotelPrice}
      taxesAndFees={taxesAndFees}
      flightPrice={flightPrice}
      duration={hotelNights}
      labelSize="small"
      cta={
        <TextButton kind="primary" fit="flex" to={offerUrl}>
          View Offer
        </TextButton>
      }
    />)}
  </Root>
}

export default connect<MappedStateProps, undefined, Props, App.State>((appState, ownProps) => {
  const flexibleSearchFilterKey = buildSuggestedDatesParamsKey(ownProps.filters?.flexibleMonths, ownProps.filters?.flexibleNights, ownProps.filters?.rooms)
  return {
    suggestedDates: getSuggestedDates(appState, flexibleSearchFilterKey, ownProps.offer.id),
  }
})(BedbankOfferTilePriceDetails)
